import { FC, useRef } from 'react';

import Card from 'components/primitives/Card';
import ChevronLeft from 'components/icons/ChevronLeft';
import ChevronRight from 'components/icons/ChevronRight';
import Loading from 'components/primitives/Loading';
import { routesSettings } from 'constants/routes';
import useIsMobile from 'hooks/useIsMobile';
import { useNavigate } from 'react-router-dom';
import { TopRent } from 'types/rent';
import { getNetworkImage } from 'utils/assets';
import { TopItemSectionViewProps } from './TopItemSection.props';

const MAX_TOP_ITEMS = 10;

const TopItemSectionView: FC<TopItemSectionViewProps> = ({
  isLoading,
  data,
}) => {
  const navigate = useNavigate();

  const isMobile = useIsMobile();

  const scrollRef = useRef<HTMLDivElement>(null);

  const onCardClick = (id?: string) => {
    navigate(routesSettings.MAIN_RENT_PRODUCT.getPath(id!), {
      replace: false,
    });
  };

  const scrollByAmount = (direction: 'left' | 'right') => {
    const container = scrollRef.current;
    if (!container) {
      return;
    }
    // Scroll by roughly the visible width so each click advances a "page".
    const amount = container.clientWidth * 0.8;
    container.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  };

  const items = (data ?? []).slice(0, MAX_TOP_ITEMS);

  return (
    <div className="w-full my-5 px-4 md:my-10 md:px-24">
      <div className="flex flex-col gap-3">
        <div className="flex-between">
          <p className="font-primary text-gray-700 text-lg justify-between font-bold md:text-3xl">
            Top Items
          </p>
        </div>
        {isLoading ? (
          <Loading />
        ) : (
          <div className="flex items-center gap-2 md:gap-4 w-full">
            {!isMobile && items.length > 0 && (
              <button
                type="button"
                aria-label="Scroll left"
                onClick={() => scrollByAmount('left')}
                className="shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-md border border-gray-100 hover:bg-gray-50">
                <ChevronLeft w="10" h="14" />
              </button>
            )}
            <div
              ref={scrollRef}
              className="flex-1 min-w-0 flex flex-nowrap items-stretch gap-4 md:gap-8 overflow-x-auto overflow-y-hidden scroll-smooth pt-4 pb-8 px-2 scrollbar-hidden">
              {items.map((topItem, index) => {
                const item = topItem.topItem;
                return (
                  <div
                    key={`${item._id}-${index}`}
                    className="shrink-0 w-40 h-60 md:w-52 md:h-72 [&>div]:!h-full [&>div]:!w-full [&>div]:!mt-0 [&_.card-body]:overflow-hidden">
                    <Card
                      img={getNetworkImage(
                        item?.featuredPhoto ?? item?.photos[0],
                      )}
                      title={item?.title}
                      description={item?.description}
                      isMobile={isMobile}
                      onCardClick={() => onCardClick(item._id)}
                    />
                  </div>
                );
              })}
            </div>
            {!isMobile && items.length > 0 && (
              <button
                type="button"
                aria-label="Scroll right"
                onClick={() => scrollByAmount('right')}
                className="shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-md border border-gray-100 hover:bg-gray-50">
                <ChevronRight w="10" h="14" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TopItemSectionView;
